import { useState } from 'react'
import styled, { css } from 'styled-components'

import { Badge, CloseIcon, Input, Plus } from '@syconium/little-miss-figgy'

import { PREVIEW_PANEL_GRAY } from './PreviewPanelDialog.styles'

export const ProductTagInput = ({
  tags,
  disabled,
  updateFormValue,
}: {
  tags: string | null
  disabled: boolean
  updateFormValue: (name: string, val: string | boolean) => void
}) => {
  const [input, setInput] = useState<string>('')

  const handleOnAddClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()

    const trimmedInput = input.trim()

    if (trimmedInput) {
      updateFormValue('productData', tags ? `${tags},${trimmedInput}` : trimmedInput)
    }

    setInput('')
  }

  const tagsToArray = tags
    ? tags
        .split(',')
        .filter(tag => tag !== 'include-unavailable')
        .map(tag => tag.trim())
    : []
  const includeUnavailable = tags?.includes('include-unavailable') || false

  const removeTag = (tag: string) => {
    updateFormValue('productData', tagsToArray.filter(t => t !== tag).join(','))
  }

  return (
    <>
      <InputWrapper>
        <Input
          id='product-tag'
          name='product-tag'
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={
            disabled
              ? 'Enable Preview Mode to Edit'
              : includeUnavailable
              ? `Tags will be ignored while 'include-unavailable' is active`
              : 'e.g. launch-polo-shop'
          }
          disabled={includeUnavailable || disabled}
        />
        {!includeUnavailable && !disabled ? (
          <AddButton onClick={handleOnAddClick} disabled={!input.trim()}>
            ADD
          </AddButton>
        ) : null}
      </InputWrapper>

      <TagsWrapper>
        {tagsToArray.map(tag => (
          <Tag
            variant='pill'
            onClick={() => removeTag(tag)}
            $disabled={includeUnavailable || disabled}
            key={crypto.randomUUID()}
          >
            {tag} <CloseIcon height='11' width='11' />
          </Tag>
        ))}
        <IncludeUnavailableTag
          variant='pill'
          onClick={() => {
            setInput('')
            if (includeUnavailable) {
              removeTag('include-unavailable')
            } else {
              updateFormValue(
                'productData',
                tags ? `${tags},${'include-unavailable'}` : 'include-unavailable'
              )
            }
          }}
          $active={includeUnavailable}
          $disabled={disabled}
        >
          {!includeUnavailable ? <PlusIcon $disabled={disabled} /> : null}
          include-unavailable
          {includeUnavailable ? <CloseIcon height='11' width='11' /> : null}
        </IncludeUnavailableTag>
      </TagsWrapper>
    </>
  )
}

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    width: 100%;
  }
`

const AddButton = styled.button`
  background-color: ${o => o.theme.color.fill.strong};
  padding: ${o => o.theme.spacing(1, 3)};
  color: ${o => o.theme.color.text.highContrast.on.darkImage};
  border-radius: ${o => o.theme.borderRadius.sm};
  position: absolute;
  font-size: 11px;
  font-weight: 600;
  right: ${o => o.theme.spacing(4)};
  transition:
    background-color 0.1s,
    color 0.1s;

  &:disabled {
    background-color: ${o => o.theme.color.fill.disabled.subtle};
    color: ${o => o.theme.color.text.tertiary.on.background};
    pointer-events: none;
  }
`

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
  font-size: 13px;
  margin-top: ${o => o.theme.spacing(1)};

  > div {
    text-transform: none;
    cursor: pointer;
  }
`

const Tag = styled(Badge)<{ $disabled?: boolean }>`
  box-shadow: 0px 1px 3px 1px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.07em;
  min-height: 31px;
  background-color: ${o => o.theme.color.fill.subtle};
  user-select: none;

  ${o =>
    o.$disabled &&
    css`
      background-color: ${o => o.theme.color.fill.background};
      text-decoration: line-through;
      pointer-events: none;
      color: ${PREVIEW_PANEL_GRAY};
    `}
`

const IncludeUnavailableTag = styled(Tag)<{ $active: boolean; $disabled: boolean }>`
  border: ${o => (o.$disabled || o.$active ? 'none' : '1px dashed black')};
  background: ${o =>
    o.$disabled
      ? o.theme.color.fill.background
      : o.$active
      ? o.theme.color.fill.strong
      : o.theme.color.fill.background};
  color: ${o =>
    o.$disabled
      ? PREVIEW_PANEL_GRAY
      : o.$active
      ? o.theme.color.fill.background
      : o.theme.color.fill.strong};
  pointer-events: ${o => (o.$disabled ? 'none' : 'auto')};
`

const PlusIcon = styled(Plus)<{ $disabled: boolean }>`
  g {
    stroke: ${o => (o.$disabled ? PREVIEW_PANEL_GRAY : 'currentColor')};
  }
`
